"use client";

import { useState } from "react";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ArrowUp,
  ArrowDown,
  Edit,
  Eye,
  EyeOff,
  Trash,
  GripVertical,
  Loader2,
} from "lucide-react";
import { OffersList } from "./OffersList";
import { SectionItemsList } from "./SectionItemsList";
import type { SectionsOutput } from "@/interfaces/sections";
import type { OffersOutput } from "@/interfaces/offers";
import type { ProductsOutput } from "@/interfaces/products";
import type {
  SectionItemOutput,
  SectionItemType,
} from "@/interfaces/section-items";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface SectionItemProps {
  section: SectionsOutput;
  offers: OffersOutput[];
  sectionItems: SectionItemOutput[];
  totalSections: number;
  onMoveSection: (sectionId: string, direction: "up" | "down") => void;
  onToggleVisibility: (sectionId: string) => void;
  onEdit: (section: SectionsOutput) => void;
  onDelete: (sectionId: string) => void;
  onAddOffer: (sectionId: string) => void;
  onToggleOfferVisibility: (offerId: string) => void;
  onDeleteOffer: (offerId: string) => void;
  onMoveOffer?: (offerId: string, direction: "up" | "down") => void;
  onAddSectionItem: (
    sectionId: string,
    itemId: string,
    itemType: SectionItemType,
    details?: ProductsOutput | OffersOutput
  ) => void;
  onToggleSectionItemVisibility: (itemId: string) => void;
  onDeleteSectionItem: (itemId: string) => void;
  onMoveSectionItem: (itemId: string, direction: "up" | "down") => void;
}

export function SectionItem({
  section,
  offers,
  sectionItems,
  totalSections,
  onMoveSection,
  onToggleVisibility,
  onEdit,
  onDelete,
  onAddOffer,
  onToggleOfferVisibility,
  onDeleteOffer,
  onMoveOffer,
  onAddSectionItem,
  onToggleSectionItemVisibility,
  onDeleteSectionItem,
  onMoveSectionItem,
}: SectionItemProps) {
  const [isLoading, setIsLoading] = useState(false);
  const sectionOffers = offers.filter(
    (offer) => offer.sectionId === section.id
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: section.id,
    data: { type: "section", section },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    opacity: isDragging ? 0.8 : 1,
  } as const;

  // wrapper p/ simular loading nas ações
  const withLoading = async (callback: () => Promise<void> | void) => {
    setIsLoading(true);
    try {
      await callback();
    } finally {
      setTimeout(() => setIsLoading(false), 300);
    }
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`${!section.isVisible ? "opacity-60" : ""} ${
        isDragging ? "shadow-xl scale-105 rotate-1 z-50" : "hover:shadow-md"
      } mb-4 transition-all duration-200 border-l-4 ${
        !section.isVisible ? "border-l-gray-300" : "border-l-[#EE7B11]"
      } rounded-lg overflow-hidden`}
    >
      <CardHeader className="pb-3 bg-gradient-to-r from-gray-50 to-gray-100 border-b">
        {/* Cabeçalho com quebra controlada */}
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-between sm:items-start w-full">
          {/* Área do título/descrição */}
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div
              {...attributes}
              {...listeners}
              className={`cursor-grab mt-1 p-2 rounded-lg transition-colors ${
                isDragging
                  ? "bg-[#EE7B11] text-white"
                  : "text-gray-400 hover:text-white hover:bg-[#EE7B11] active:cursor-grabbing"
              }`}
              title="Arraste para reordenar seção"
            >
              <GripVertical className="h-5 w-5" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1 flex-wrap">
                {/* Título com truncamento agressivo em sm+ */}
                <h3 className="text-lg font-semibold text-gray-800 flex-1 min-w-0 sm:max-w-[60%] truncate">
                  {section.name}
                </h3>

                {isLoading && (
                  <Loader2 className="h-4 w-4 animate-spin text-[#EE7B11]" />
                )}

                {!section.isVisible && (
                  <Badge
                    variant="outline"
                    className="text-gray-500 border-gray-300"
                  >
                    Oculto
                  </Badge>
                )}

                {isDragging && (
                  <Badge
                    variant="secondary"
                    className="bg-[#EE7B11]/10 text-[#EE7B11] border-[#EE7B11]/20"
                  >
                    Posição {section.position}
                  </Badge>
                )}
              </div>

              {section.description && (
                <div
                  className="text-sm text-gray-600 rich-text-content line-clamp-2"
                  dangerouslySetInnerHTML={{ __html: section.description }}
                />
              )}
            </div>
          </div>

          {/* Ações: podem quebrar linha quando faltar espaço */}
          <div className="flex items-center gap-1 sm:gap-1.5 flex-wrap justify-end mt-2 sm:mt-0 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => withLoading(() => onMoveSection(section.id, "up"))}
              disabled={section.position === 1 || isLoading}
              className="h-8 w-8 p-0 text-gray-500 hover:text-[#EE7B11] hover:bg-[#EE7B11]/10"
              title="Mover seção para cima"
            >
              <ArrowUp className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                withLoading(() => onMoveSection(section.id, "down"))
              }
              disabled={section.position === totalSections || isLoading}
              className="h-8 w-8 p-0 text-gray-500 hover:text-[#EE7B11] hover:bg-[#EE7B11]/10"
              title="Mover seção para baixo"
            >
              <ArrowDown className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => withLoading(() => onEdit(section))}
              disabled={isLoading}
              className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              title="Editar seção"
            >
              <Edit className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => withLoading(() => onToggleVisibility(section.id))}
              disabled={isLoading}
              className={`h-8 w-8 p-0 ${
                section.isVisible
                  ? "text-green-600 hover:text-green-700 hover:bg-green-50"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
              title={section.isVisible ? "Ocultar seção" : "Mostrar seção"}
            >
              {section.isVisible ? (
                <Eye className="h-4 w-4" />
              ) : (
                <EyeOff className="h-4 w-4" />
              )}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => withLoading(() => onDelete(section.id))}
              disabled={isLoading}
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
              title="Excluir seção"
            >
              <Trash className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-5">
        {/* Ofertas legadas (suporte) */}
        {sectionOffers.length > 0 && (
          <div className="mb-6">
            <h4 className="font-medium mb-3 text-orange-600">
              Ofertas (Sistema Legado)
            </h4>
            <OffersList
              sectionId={section.id}
              offers={sectionOffers}
              onAddOffer={onAddOffer}
              onToggleVisibility={onToggleOfferVisibility}
              onDelete={onDeleteOffer}
              onMoveOffer={onMoveOffer}
            />
          </div>
        )}

        {/* Novos Section Items */}
        <SectionItemsList
          sectionId={section.id}
          items={sectionItems}
          onAddItem={onAddSectionItem}
          onToggleVisibility={onToggleSectionItemVisibility}
          onDelete={onDeleteSectionItem}
          onMove={onMoveSectionItem}
        />
      </CardContent>
    </Card>
  );
}
